import { IInteractionsCount } from "@edgetier/watchtower-types";
import { differenceInDays } from "date-fns";
import { TOP_TRIGGERS_PER_DAY, TOP_TRIGGERS_PER_TRIGGER } from "./phrase-triggers-interactions-tagged-chart.constants";

/**
 * Find the top five phrase trigger across all days based on number of interactions.
 */
export const getTopPhraseTriggers = (queryData: IInteractionsCount<any>[] | undefined) => {
    const totalCountAcrossAllDays: Record<string, number> = (queryData ?? []).reduce((previousData, currentEntry) => {
        return {
            ...previousData,
            [currentEntry.group[1]]: (previousData[currentEntry.group[1]] ?? 0) + currentEntry.count,
        };
    }, {} as Record<string, number>);

    return Object.entries(totalCountAcrossAllDays).sort(([, one], [, two]) => {
        if (one < two) return 1;
        if (one > two) return -1;
        return 0;
    });
};

/**
 * Transform the interaction count data to be suitable for chart plotting.
 */
export const formatChartData = (queryData: IInteractionsCount<any>[] | undefined) => {
    if (typeof queryData !== "undefined") {
        const topTriggers = getTopPhraseTriggers(queryData).slice(0, TOP_TRIGGERS_PER_TRIGGER);

        return topTriggers.map(([topTriggerKey, count]) => ({
            group: topTriggerKey,
            count,
        }));
    }
    return [];
};

/**
 * Transform the interaction count data to be suitable for stacked chart plotting.
 */
export const formatStackChartData = (queryData: IInteractionsCount<any>[] | undefined) => {
    if (typeof queryData !== "undefined") {
        const topPhraseTriggersByInteractions = getTopPhraseTriggers(queryData).slice(0, TOP_TRIGGERS_PER_DAY);

        const chartData: Record<string, any> = queryData.reduce((previousData, currentEntry) => {
            const [dateGroup, phraseTriggerId] = currentEntry.group;
            const dateTime = new Date(dateGroup).getTime();
            const topPhraseTriggerIndex = topPhraseTriggersByInteractions.findIndex(
                ([triggerId]) => triggerId === phraseTriggerId
            );

            if (typeof previousData[dateTime] === "undefined") {
                return {
                    ...previousData,
                    [dateTime]: {
                        dateTime,
                        ...(topPhraseTriggerIndex >= 0 ? { [phraseTriggerId]: currentEntry.count } : { others: 0 }),
                    },
                };
            } else {
                return {
                    ...previousData,
                    [dateTime]: {
                        ...previousData[dateTime],
                        ...(topPhraseTriggerIndex >= 0
                            ? { [phraseTriggerId]: currentEntry.count }
                            : { others: (previousData[dateTime].others ?? 0) + currentEntry.count }),
                    },
                };
            }
        }, {} as Record<string, any>);

        return Object.values(chartData);
    }
    return [];
};

/**
 * Determine the tick interval from the number of days from date range. The interval is different for
 * every three weeks or 21 days
 * @param startDateTime
 * @param endDateTime
 */
export const getDateTimeXAxisTickInterval = (startDateTime: Date, endDateTime: Date) => {
    const differenceDays = differenceInDays(endDateTime, startDateTime);

    if (differenceDays <= 21) return 0;
    if (differenceDays <= 42) return 1;
    if (differenceDays <= 63) return 2;
    if (differenceDays <= 74) return 3;
    if (differenceDays <= 84) return 4;

    return 5;
};
